import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {SupportTicketModel} from '../../../../model/support/support-ticket.model';
import {
  SearchableOrder,
  SearchableState,
  useSearchableStateWithOrdering
} from '../../../../state/searchable-state';
import {
  SupportPageHooks,
} from './support-page-hooks';

export type SupportPageState = {
  tickets: SupportTicketModel[]
  searchableState: SearchableState
  extractAvailableValues: (column: string) => string[]
  total: number

  refreshTickets: () => Promise<void>

  isAttachmentDrawerOpen: boolean
  setAttachmentDrawerOpen: (value: boolean) => void

  isDrawerOpen: boolean
  selectedTicket: SupportTicketModel | null
  setDrawerOpen: (value: boolean) => void
  setSelectedTicket: (ticket: SupportTicketModel | null) => void

  isRelatedTasksTicketOpen: boolean
  setRelatedTasksTicketOpen: (value: boolean) => void

  translateLocal: (key: string) => string
  translate: (key: string) => string
}

export const useSupportPageState = (): SupportPageState => {
  const {t} = useTranslation()

  const availableValues = ['support_ticket.organization_name', 'support_ticket.account_name', 'support_ticket.module', 'support_ticket.status', 'support_ticket.ticket_type', 'support_ticket.error_type']
  const fullTextSearchColumns = ['support_ticket.id', 'support_ticket.title', 'support_ticket.description', 'support_ticket.organization_name', 'support_ticket.account_name']
  const initialOrdering: SearchableOrder[] = [{
    orderBy: 'support_ticket.created_date_timestamp',
    orderValues: [],
    orderDirection: 'DESCENDING'
  }]

  const searchableState = useSearchableStateWithOrdering(availableValues, fullTextSearchColumns, initialOrdering)
  const [tickets, setTickets] = SupportPageHooks.useSupportTickets(searchableState.searchable)
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
  const [attachmentDrawerOpen, setAttachmentDrawerOpen] = useState<boolean>(false)
  const [selectedTicket, setSelectedTicket] = useState<SupportTicketModel | null>(null)

  const [isRelatedTasksTicketOpen, setRelatedTasksTicketOpen] = useState(false)

  const refreshTickets = async () => {
    const refreshedTickets = await SupportPageHooks.fetchSupportTickets(searchableState.searchable)

    if(selectedTicket !== null) {
      const updatedSelectedTicket = refreshedTickets.items.find(it => it.id === selectedTicket.id) ?? selectedTicket
      setSelectedTicket(updatedSelectedTicket)
    }

    setTickets(refreshedTickets)
  }

  return {
    tickets: tickets?.items ?? [],
    total: tickets?.total ?? 0,
    searchableState: searchableState,
    isDrawerOpen: drawerOpen,
    selectedTicket: selectedTicket,
    isAttachmentDrawerOpen: attachmentDrawerOpen,
    isRelatedTasksTicketOpen: isRelatedTasksTicketOpen,
    setRelatedTasksTicketOpen: setRelatedTasksTicketOpen,
    setAttachmentDrawerOpen: setAttachmentDrawerOpen,
    refreshTickets: refreshTickets,
    setSelectedTicket: setSelectedTicket,
    setDrawerOpen: setDrawerOpen,
    extractAvailableValues: (column: string) => {
      return searchableState.extractAvailableValues(tickets?.available ?? {}, column)
    },
    translateLocal: (key) => t(`support-page.${key}`),
    translate: (key) => t(key)
  }
}

